import React, { useState, useEffect } from 'react';
import { Page } from '../types';
import { getArticles, getReports, slugify, getMediaUrl } from '../services/api';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate, Link } from 'react-router-dom';
import { formatDate, formatDateTime } from '../utils/formatDate';
import { formatCategoryName } from '../utils/formatCategory';

interface HomeProps {
  openPdf: (url: string) => void;
}

interface NewsItem {
  id: string;
  title: string;
  date: string;
  cat: string;
  imageUrl: string;
  slug: string;
  author: string;
  writerAvatar?: string;
  excerpt: string;
}

const Home: React.FC<HomeProps> = ({ openPdf }) => {
  const { t } = useTranslation();
  const { locale } = useLanguage();
  const navigate = useNavigate();

  const [latestNews, setLatestNews] = useState<NewsItem[]>([]);
  const [kalemArticles, setKalemArticles] = useState<NewsItem[]>([]);
  const [latestReports, setLatestReports] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [newsRes, reportsRes, kalemRes] = await Promise.all([
          getArticles(undefined, locale, 1, 12),
          getReports(undefined, locale, 1, 6),
          getArticles(undefined, locale, 1, 6, 'KadininKalemi')
        ]);

        const rawArticles = newsRes.data || [];
        
        const mainNews = rawArticles
          .filter((item: any) => item.category !== 'KadininKalemi')
          .slice(0, 8)
          .map(formatArticle);

        const kalemNews = (kalemRes.data || []).map(formatArticle);

        setLatestNews(mainNews);
        setKalemArticles(kalemNews);

        const reportItems = (reportsRes.data || []).map((item: any) => ({
            id: item.documentId || item.id,
            title: item.title,
            description: item.description,
            date: formatDate(item.reportDate, locale),
            size: item.file?.size ? `${(item.file.size / 1024).toFixed(1)} MB` : 'PDF',
            pages: t('resources.report'),
            fileUrl: item.file?.url
          }));
        setLatestReports(reportItems);

      } catch (error) {
        console.error("Failed to load home data", error);
      }
    };
    fetchData();
  }, [locale]);

  const formatArticle = (item: any): NewsItem => {
    const category: string = formatCategoryName(item.category, t);

    const imageUrl = item.coverImage?.url
      ? getMediaUrl(item.coverImage.url)
      : `https://picsum.photos/seed/${item.id}/800/600`;

    const excerpt = item.content?.[0]?.children?.[0]?.text || '...';

    const writerName = item.writer?.name || item.author || 'TD';
    const writerAvatar = item.writer?.avatar?.url 
      ? getMediaUrl(item.writer.avatar.url) 
      : undefined;

    const isEvent = item.category === 'Etkinlikler';
    const displayDate = (isEvent && item.eventDate)
      ? formatDateTime(item.eventDate, locale)
      : formatDate(item.publishedDate, locale);

    return {
      id: item.documentId || item.id,
      title: item.title,
      date: displayDate,
      cat: category,
      imageUrl: imageUrl,
      slug: item.slug || slugify(item.title) || item.documentId || item.id,
      author: writerName,
      writerAvatar: writerAvatar,
      excerpt: excerpt
    };
  };



  const featuredKalemList = kalemArticles.slice(0, 2);
  const sideKalem = kalemArticles.slice(2);

  return (
    <div className="animate-in fade-in duration-1000">
      {/* 1. Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-brand-purple-900 text-white py-20 lg:py-28">
        {/* Abstract Background Elements */}
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] bg-brand-purple-600 rounded-full blur-[120px] opacity-40 animate-pulse"></div>
          <div className="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] bg-brand-green-500 rounded-full blur-[150px] opacity-20"></div>
          <div className="absolute top-[20%] right-[10%] w-[100px] h-[100px] bg-brand-yellow-400 rounded-full blur-[60px] opacity-30"></div>

          {/* Subtle Grid Pattern Overlay */}
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl lg:max-w-none lg:grid lg:grid-cols-12 lg:gap-10 xl:gap-14 lg:items-center space-y-10 lg:space-y-0">
            {/* Sol Taraf (Masaüstü: Badge + Başlık) */}
            <div className="lg:col-span-7 space-y-8">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm">
                <span className="w-2 h-2 bg-brand-yellow-400 rounded-full animate-ping"></span>
                <span className="text-xs font-bold uppercase tracking-widest text-brand-yellow-400">{t('home.hero_badge')}</span>
              </div>

              <h1 className="text-3xl min-[400px]:text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-serif font-bold leading-[1.15] tracking-tight break-words">
                {(t('home.hero_title') as string).split(',').map((part: string, i: number) => (
                  <React.Fragment key={i}>
                    {i > 0 && <br />}
                    <span className={i > 0 ? 'bg-gradient-to-r from-brand-yellow-400 to-brand-green-500 bg-clip-text text-transparent' : ''}>
                      {part}{i === 0 && ','}
                    </span>
                  </React.Fragment>
                ))}
              </h1>
            </div>

            {/* Sağ Taraf (Masaüstü: Şık Cam Panel İçinde Alt Metin + Butonlar) */}
            <div className="lg:col-span-5">
              <div className="space-y-8 lg:p-7 xl:p-9 lg:rounded-[2.5rem] lg:bg-white/[0.05] lg:backdrop-blur-xl lg:border lg:border-white/15 lg:shadow-2xl lg:shadow-purple-950/50">
                <p className="text-lg sm:text-xl lg:text-base xl:text-lg text-brand-purple-50/90 font-light leading-relaxed lg:border-l-2 lg:border-brand-yellow-400/80 lg:pl-5 rtl:lg:border-l-0 rtl:lg:border-r-2 rtl:lg:border-brand-yellow-400/80 rtl:lg:pr-5">
                  {t('home.hero_subtitle')}
                </p>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    onClick={() => navigate(`/${locale}/biz-kimiz`)}
                    className="flex-1 bg-brand-yellow-400 text-brand-purple-900 px-4 py-4 rounded-2xl font-bold text-sm xl:text-base hover:bg-white transition-all shadow-xl active:scale-95 flex items-center justify-center group whitespace-nowrap min-w-0"
                  >
                    <span>{t('home.hero_cta1')}</span>
                    <svg className="w-4 h-4 ms-2 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </button>
                  <button
                    onClick={() => navigate(`/${locale}/raporlar`)}
                    className="flex-1 bg-white/10 border-2 border-white/20 text-white px-4 py-4 rounded-2xl font-bold text-sm xl:text-base hover:bg-white hover:text-brand-purple-900 transition-all text-center whitespace-nowrap min-w-0"
                  >
                    {t('home.hero_cta2')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Latest News Section (Basın & Kültür-Sanat) */}
      <section className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-6">
            <div className="space-y-4 text-left flex flex-col items-start">
              <span className="text-brand-green-500 font-bold uppercase tracking-widest text-sm">{t('nav.news')}</span>
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-brand-purple-900 leading-tight">{t('home.latest_news')}</h2>
              <div className="w-20 h-1.5 bg-brand-yellow-400 rounded-full"></div>
            </div>
            <button
              onClick={() => navigate(`/${locale}/basin`)}
              className="self-end px-6 py-3 rounded-xl border border-brand-purple-100 text-brand-purple-600 font-bold hover:bg-brand-purple-50 transition-colors flex items-center"
            >
              {t('home.all_news')}
              <svg className="w-4 h-4 ms-2 rtl:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {latestNews.length > 0 ? (
              latestNews.map((news: NewsItem) => (
                <Link
                  key={news.id}
                  to={`/${locale}/haberler/${news.slug || news.id}`}
                  className="group cursor-pointer bg-brand-neutral-bg rounded-[2rem] p-3 transition-all hover:bg-white hover:shadow-2xl hover:shadow-brand-purple-500/10 border border-transparent hover:border-brand-purple-50 flex flex-col h-full"
                >
                  <div className="relative aspect-[3/2] rounded-[1.5rem] overflow-hidden mb-4 shrink-0">
                    <img src={news.imageUrl} alt={news.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute top-3 left-3">
                      <span className="bg-brand-purple-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-tighter shadow-lg">{news.cat}</span>
                    </div>
                  </div>
                  <div className="px-2 pb-2 space-y-2 flex-grow flex flex-col">
                    <p className="text-xs text-brand-green-500 font-semibold">{news.date}</p>
                    <h3 className="text-base font-serif font-bold text-brand-purple-900 group-hover:text-brand-purple-600 transition-colors line-clamp-3 leading-snug">{news.title}</h3>
                  </div>
                </Link>
              ))
            ) : (
              <div className="col-span-full py-10 text-center text-gray-400 font-light italic">
                {t('home.no_news')}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 3. Kadının Kalemi Highlight Section (Editorial Layout) */}
      <section className="py-24 bg-brand-purple-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-brand-purple-900">{t('home.kalem_title')}</h2>
            <p className="text-gray-600 italic">{t('home.kalem_quote')}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">
            {/* Featured Articles (2 items) */}
            <div className="lg:col-span-7 flex flex-col gap-6">
              {featuredKalemList.length > 0 ? featuredKalemList.map((item) => (
                <Link
                  key={item.id}
                  className="bg-white rounded-[2rem] shadow-lg overflow-hidden group cursor-pointer flex flex-col md:flex-row md:h-64"
                  to={`/${locale}/haberler/${item.slug || item.id}`}
                >
                  <div className="h-48 md:h-full md:w-2/5 overflow-hidden shrink-0">
                    <img src={item.imageUrl} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" alt="Yazı" />
                  </div>
                  <div className="p-6 md:p-8 flex flex-col justify-center h-full space-y-4 md:w-3/5">
                    <div>
                      <h3 className="text-2xl font-serif font-bold text-brand-purple-900 hover:text-brand-purple-600 transition-colors leading-snug line-clamp-3">
                        {item.title}
                      </h3>
                      {item.excerpt && (
                        <p className="text-sm text-gray-600 line-clamp-2 mt-2">{item.excerpt}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-3 border-t border-gray-100 pt-4 mt-4">
                      {item.writerAvatar ? (
                        <img src={item.writerAvatar} alt={item.author} className="w-14 h-14 rounded-full object-cover border border-gray-200" />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-brand-purple-100 flex items-center justify-center text-brand-purple-700 font-bold">{item.author ? item.author.substring(0, 2).toUpperCase() : 'TD'}</div>
                      )}
                      <div>
                        <p className="text-sm font-bold text-brand-purple-900">{item.author}</p>
                        <p className="text-[10px] text-gray-400">{t('kalem_page.author')}</p>
                      </div>
                    </div>
                  </div>
                </Link>
              )) : (
                <div className="text-center text-gray-400 py-10">{t('home.kalem_no_article')}</div>
              )}
            </div>

            {/* Side Column List */}
            <div className="lg:col-span-5 flex flex-col justify-between">
              <div className="space-y-3">
                {sideKalem.map((item: NewsItem, i: number) => (
                  <Link key={item.id} className="flex items-center gap-4 group cursor-pointer bg-white p-3.5 rounded-2xl shadow-sm hover:shadow-md transition-shadow h-[100px]" to={`/${locale}/haberler/${item.slug || item.id}`}>
                    <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-gray-200">
                      <img src={item.imageUrl} className="w-full h-full object-cover group-hover:scale-110 transition-transform" alt={item.title} />
                    </div>
                    <div className="flex flex-col justify-center space-y-1.5 py-0.5 flex-1">
                      <h4 className="text-sm font-serif font-bold text-brand-purple-900 group-hover:text-brand-purple-600 transition-colors line-clamp-2 leading-snug">{item.title}</h4>
                      <div className="flex items-center space-x-2">
                        {item.writerAvatar ? (
                          <img src={item.writerAvatar} alt={item.author} className="w-4 h-4 rounded-full object-cover" />
                        ) : (
                           <div className="w-4 h-4 rounded-full bg-brand-purple-100 flex items-center justify-center text-brand-purple-700 text-[8px] font-bold">{item.author ? item.author.substring(0, 1).toUpperCase() : 'T'}</div>
                        )}
                        <p className="text-[10px] text-gray-500 font-medium">{item.author}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              <button onClick={() => navigate(`/${locale}/kadinin-kalemi`)} className="w-full mt-4 py-3.5 text-brand-purple-600 font-bold border-2 border-brand-purple-200 rounded-2xl hover:bg-brand-purple-600 hover:text-white transition-all text-sm">
                {t('home.kalem_all_articles')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Reports Preview Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center mb-16 gap-6">
            <h2 className="text-4xl font-serif font-bold text-brand-purple-900">{t('home.reports_title')}</h2>
            <p className="text-gray-500 max-w-lg text-center md:text-left font-light">
              {t('home.reports_desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {latestReports.length > 0 ? latestReports.map((report: any, i: number) => (
              <div
                key={i}
                onClick={() => report.fileUrl && openPdf(getMediaUrl(report.fileUrl))}
                className="group flex items-center p-8 bg-brand-neutral-bg rounded-[2.5rem] border border-transparent hover:border-brand-green-500/20 hover:bg-white transition-all cursor-pointer"
              >
                <div className="w-20 h-24 bg-brand-purple-100 rounded-xl flex items-center justify-center text-brand-purple-700 group-hover:bg-brand-green-500 group-hover:text-white transition-colors relative">
                  <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div className="absolute -bottom-2 -right-2 bg-brand-yellow-400 text-[8px] font-bold text-brand-purple-900 px-1.5 py-0.5 rounded uppercase">PDF</div>
                </div>
                <div className="ms-8 flex-1">
                  <h4 className="text-xl font-serif font-bold text-brand-purple-900 group-hover:text-brand-purple-600 mb-2 transition-colors leading-snug">{report.title}</h4>
                  <div className="flex items-center space-x-4 text-xs text-gray-400 font-medium">
                    <span>{report.pages}</span>
                    <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                    <span>{report.size}</span>
                  </div>
                </div>
                <button className="ms-4 w-12 h-12 rounded-full border border-brand-purple-100 flex items-center justify-center text-brand-purple-500 group-hover:bg-brand-purple-500 group-hover:text-white transition-all">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                </button>
              </div>
            )) : (
              <div className="col-span-full text-center py-8 text-gray-500">{t('home.reports_empty')}</div>
            )}
          </div>

          <div className="mt-12 text-center">
            <button onClick={() => navigate(`/${locale}/raporlar`)} className="text-brand-green-500 font-bold hover:underline underline-offset-8">{t('home.all_reports')}</button>
          </div>
        </div>
      </section>

      {/* 5. Call to Action: Solidarity Message */}
      <section className="py-24 bg-brand-yellow-400 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-2xl"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-brand-purple-500/10 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl"></div>

        <div className="max-w-4xl mx-auto px-4 text-center relative z-10 space-y-10">
          <h2 className="text-3xl sm:text-4xl md:text-6xl font-serif font-bold text-brand-purple-900 leading-tight break-words">
            {t('home.cta_title')}
          </h2>
          <p className="text-xl text-brand-purple-900/70 max-w-2xl mx-auto leading-relaxed">
            {t('home.cta_desc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center pt-4">
            <button
              onClick={() => navigate(`/${locale}/iletisim`)}
              className="bg-brand-purple-900 text-white px-12 py-5 rounded-2xl font-bold text-xl hover:bg-brand-purple-700 transition-all shadow-2xl hover:-translate-y-1"
            >
              {t('home.cta_btn1')}
            </button>
            {t('home.cta_btn2') ? (
              <button
                onClick={() => navigate(`/${locale}/iletisim`)}
                className="bg-white text-brand-purple-900 px-12 py-5 rounded-2xl font-bold text-xl hover:bg-brand-purple-50 transition-all shadow-md"
              >
                {t('home.cta_btn2')}
              </button>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
